import React, { useCallback, useState } from 'react';
import { Button } from 'reactstrap';
import { useHistory } from 'react-router-dom';
import {
  individual_column_style,
  individual_header_style,
  icon_column_style,
  individual_icon_header_style
} from '../../../../../../../../assets/styles/table_custom_style';
import BootstrapTable from 'react-bootstrap-table-next';
import ToolkitProvider from 'react-bootstrap-table2-toolkit';

import ModalConfirmation from 'components/Modal/ConfirmationModal';

import OpportunityService from '../../../../../services/opportunityService';
import { toast } from 'react-toastify';

import './styles.css';
import { toBrazilianFullDate } from 'views/CRM/shared/utils/date';
import OpportunityHistoryModal from '../OpportunityHistoryModal';
import { maskPhoneNumber } from 'views/CRM/shared/utils/phoneNumberHelpers';
import { maskCpf } from 'views/CRM/shared/utils/cpfCnpjHelpers';
import { maskCnpj } from 'views/CRM/shared/utils/cpfCnpjHelpers';
import { formatCurrency } from 'utils/fuctions/formatCurrency';
import { verifyRoles } from 'components/Authentication/VerifyRoles';
import OpportunityActionsModal from '../../../OpportunityActionsModal';

const ListOpportunitiesTable = ({
  opportunities,
  reloadOpportunities,
  customFields,
  transferOpportunities,
  selectedRows,
  handleOnSelectAll,
  handleOnSelect
}) => {
  const [openModalConfirmation, setOpenModalConfirmation] = useState(false);
  const [opportunityId, setOpportunityId] = useState(null);

  const formatter = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  });
  const history = useHistory();

  const handleDeleteOpportunity = useCallback(
    (id) => {
      OpportunityService.delete(id)
        .catch((err) => {
          const message =
            err?.response?.data?.message || 'Erro ao deletar a oportunidade';
          toast.error(message);
        })
        .finally(() => {
          reloadOpportunities(1);
          setOpenModalConfirmation(false);
        });
    },
    [reloadOpportunities]
  );

  const editOpportunity = (opportunity) => {
    history.push(
      `/admin/crm/opportunityEdit/${opportunity.recordTypeId}/${opportunity.id}`
    );
  };

  const controlDeleteOpportunity = (id) => {
    setOpportunityId(id);
    setOpenModalConfirmation(true);
  };

  const createDynamicColumns = () => {
    const columns = [];

    columns.push(
      {
        dataField: 'title',
        text: 'Título',
        sort: true,
        style: individual_column_style,
        headerStyle: { ...individual_header_style, cursor: 'pointer' },
        formatter: (_, row) => {
          return (
            <span
              className="opportunity-name"
              onClick={() => {
                editOpportunity(row);
              }}
            >
              {row.title}
            </span>
          );
        }
      },
      {
        dataField: 'stageName',
        text: 'Fase',
        style: individual_column_style,
        headerStyle: { ...individual_header_style, cursor: 'pointer' }
      },
      {
        dataField: 'createdAt',
        text: 'Criado em',
        style: individual_column_style,
        headerStyle: { ...individual_header_style, cursor: 'pointer' },
        formatter: (row) => {
          if (row) return toBrazilianFullDate(row);
        }
      },
      {
        dataField: 'createdByName',
        text: 'Criado por',
        style: individual_column_style,
        headerStyle: { ...individual_header_style, cursor: 'pointer' }
      },
      {
        dataField: 'responsibleName',
        text: 'Responsável',
        style: individual_column_style,
        headerStyle: { ...individual_header_style, cursor: 'pointer' }
      },
      {
        dataField: 'amount',
        text: 'Valor',
        style: individual_column_style,
        headerStyle: { ...individual_header_style, cursor: 'pointer' },
        formatter: (row) => {
          if (!row) return 'R$ 0,00';
          return formatter.format(row);
        }
      },
      {
        dataField: 'lastUpdatedAt',
        text: 'Última atualização',
        style: individual_column_style,
        headerStyle: { ...individual_header_style, cursor: 'pointer' },
        formatter: (row) => {
          if (row) return toBrazilianFullDate(row);
        }
      }
    );

    (customFields || []).forEach((field) => {
      columns.push({
        dataField: field.name,
        text: field.label,
        sort: true,
        style: individual_column_style,
        formatter: (_, row) => {
          if (field.type === 'DATE' && !!row[field.name]) {
            return new Date(row[field.name]).toLocaleDateString('pt-br');
          } else if (field.type === 'TELEPHONE' && row[field.name]) {
            return maskPhoneNumber(row[field.name]);
          } else if (field.type === 'CPF' && row[field.name]) {
            return maskCpf(row[field.name]);
          } else if (field.type === 'CNPJ' && row[field.name]) {
            return maskCnpj(row[field.name]);
          } else if (field.type === 'CURRENCY' && row[field.name]) {
            return formatCurrency(row[field.name]);
          }
          return row[`${field.name}`];
        }
      });
    });

    columns.push({
      dataField: 'edit',
      text: 'Editar',
      style: icon_column_style,
      headerStyle: individual_icon_header_style,
      formatter: (_, row) => {
        return (
          <>
            {!row.isConverted ? (
              <Button
                size="sm"
                color="info"
                onClick={() => editOpportunity(row)}
              >
                <li className="fas fa-user-edit"></li>
              </Button>
            ) : null}
          </>
        );
      }
    });

    columns.push({
      dataField: 'history',
      text: 'Histórico',
      style: icon_column_style,
      headerStyle: individual_icon_header_style,
      formatter: (_, row) => {
        return (
          <OpportunityHistoryModal
            opportunityId={row.id}
            convertOpportunityId={row.convertOpportunityId}
          />
        );
      }
    });

    columns.push({
      dataField: 'actions',
      text: 'Ações',
      style: icon_column_style,
      headerStyle: individual_icon_header_style,
      formatter: (_, row) => {
        return <OpportunityActionsModal opportunityData={row} />;
      }
    });

    columns.push({
      dataField: 'delete',
      text: 'Excluir',
      style: icon_column_style,
      headerStyle: individual_icon_header_style,
      formatter: (_, row) => {
        return (
          <>
            {!row.isConverted && verifyRoles(['DELETE_OPPORTUNITY_CRM']) ? (
              <Button
                size="sm"
                color="danger"
                onClick={() => controlDeleteOpportunity(row.id)}
              >
                <li className="fas fa-trash"></li>
              </Button>
            ) : null}
          </>
        );
      }
    });

    return [...columns];
  };

  const formattedColumns = createDynamicColumns();

  return (
    <>
      <ToolkitProvider
        data={opportunities || []}
        keyField="id"
        columns={formattedColumns}
      >
        {(props) => (
          <div className="py-4 table-responsive">
            {transferOpportunities ? (
              <BootstrapTable
                selectRow={{
                  mode: 'checkbox',
                  selected: selectedRows.map((r) => r.id),
                  onSelect: handleOnSelect,
                  onSelectAll: handleOnSelectAll
                }}
                bootstrap4={true}
                bordered={false}
                classes="align-items-center"
                {...props.baseProps}
              />
            ) : (
              <BootstrapTable
                {...props.baseProps}
                bootstrap4={true}
                bordered={false}
                classes="align-items-center"
              />
            )}
          </div>
        )}
      </ToolkitProvider>

      <ModalConfirmation
        isModalOpen={openModalConfirmation}
        onModalToggle={setOpenModalConfirmation}
        unmountOnClose={true}
        idResource={opportunityId}
        deleteResource={handleDeleteOpportunity}
        message={'Tem certeza que deseja excluir a oportunidade?'}
      />
    </>
  );
};

export default ListOpportunitiesTable;
